import React from 'react'
import { Link } from 'react-router-dom'

import { Alert, Cluster, Heading, Stack } from 'component-library-next'
import { RiFilterLine } from 'react-icons/ri'

import { FormattedDate, useIntl } from '@app/intl/Intl'
import { Pagination } from '@app/lib/components/Pagination/Pagination'
import { ProductType } from '@app/lib/display/ProductType/ProductType'
import { usePersistedState } from '@app/lib/hooks/usePersistedState'
import { Button } from '@app/lib/inputs/Button/Button'
import { Card } from '@app/lib/layout/Card/Card'
import { CardBlock } from '@app/lib/layout/Card/CardBlock'
import { CardHeader } from '@app/lib/layout/Card/CardHeader'
import { LineItem } from '@app/lib/layout/LineItem/LineItem'
import { Sidebar } from '@app/lib/layout/Sidebar/Sidebar'
import { Table, TableColumn } from '@app/lib/layout/Table/Table'
import { Text } from '@app/lib/typography/Text/Text'
import { AuditReportsFilterForm } from '@app/modules/AuditReports/AuditReportsFilterForm'
import {
  AuditReportsItem,
  useAuditReportsTable
} from '@app/modules/AuditReports/hooks/useAuditReportsTable'
import { getMessagesPath } from '@app/modules/Messages'

import './AuditReportsWidget.scss'

export type AuditReportsWidgetProps = {
  lawyerId: number
}

export const AuditReportsWidget = ({ lawyerId }: AuditReportsWidgetProps) => {
  const { formatMessage } = useIntl()
  const [showFilter, setShowFilter] = usePersistedState(`audit-reports-filter`, false)

  const { data, loading, error, query, dispatch } = useAuditReportsTable({ lawyerId })

  const columns: Array<TableColumn<AuditReportsItem>> = [
    {
      key: 'customerFullName',
      label: formatMessage({ id: 'Lawyer.AuditReports.Table.Column.Name' }),
      value: (caseItem) => (
        <Link to={getMessagesPath(caseItem.customer.id)}>
          {[caseItem.customer.firstName, caseItem.customer.lastName].filter(Boolean).join(' ')}
        </Link>
      )
    },
    {
      key: 'topic',
      label: formatMessage({ id: 'Lawyer.AuditReports.Table.Column.Topic' }),
      value: (caseItem) => (
        <Link to={getMessagesPath(caseItem.customer.id)}>
          {caseItem.customer.latestQuestion?.topic.name}
        </Link>
      )
    },
    {
      key: 'productName',
      label: formatMessage({ id: 'Lawyer.AuditReports.Table.Column.ProductName' }),
      value: (caseItem) => (
        <ProductType
          uid={caseItem.customer.membership?.membershipType.uid}
          displayName={
            caseItem.customer.membership?.membershipType.displayName ||
            caseItem.purchase.product?.name
          }
        />
      )
    },
    {
      key: 'approvedAt',
      label: formatMessage({ id: 'Lawyer.AuditReports.Table.Column.ApprovedAt' }),
      sort: query.sorts.latestQuestionApprovedAt,
      onSortChange: (direction) =>
        dispatch({ type: 'SET_SORT', payload: { latestQuestionApprovedAt: direction } }),
      value: (caseItem) => {
        return caseItem.customer.latestQuestion?.approvedAt ? (
          <FormattedDate
            day='2-digit'
            month='2-digit'
            year='numeric'
            value={caseItem.customer.latestQuestion?.approvedAt}
          />
        ) : null
      }
    },
    {
      key: 'note',
      label: formatMessage({ id: 'Lawyer.AuditReports.Table.Column.Note' }),
      value: (caseItem) => caseItem.customer.note?.note
    }
  ]

  return (
    <Card>
      <CardHeader>
        <LineItem
          label={
            <Heading size={Heading.sizes.small}>
              {formatMessage({ id: 'Lawyer.AuditReports.Heading' })}
            </Heading>
          }
          actions={
            <Cluster>
              <Button size='sm' variant='outlined' onClick={() => setShowFilter((prev) => !prev)}>
                <RiFilterLine />
                <span>
                  {formatMessage({
                    id: showFilter
                      ? 'Lawyer.AuditReports.Table.HideFilter'
                      : 'Lawyer.AuditReports.Table.ShowFilter'
                  })}
                </span>
              </Button>
            </Cluster>
          }
        />
      </CardHeader>
      {error && (
        <CardBlock>
          <Alert color={Alert.colors.warning}>
            {formatMessage({ id: 'Lawyer.AuditReports.Table.Error' })}
          </Alert>
        </CardBlock>
      )}
      <Sidebar
        sidebar={
          <AuditReportsFilterForm
            values={query.filters}
            onSubmit={(data) => dispatch({ type: 'SET_FILTER', payload: data })}
            onReset={() => dispatch({ type: 'RESET' })}
          />
        }
        isVisible={showFilter}
      >
        <Stack>
          <Table
            columns={columns}
            items={data?.cases.items || []}
            loading={loading}
            noResults={formatMessage({ id: 'Lawyer.AuditReports.Table.NoResults' })}
          />
          <Pagination
            value={{ ...query.pagination, total: data?.cases.total || 0 }}
            onChange={(data) => dispatch({ type: 'SET_PAGINATION', payload: data })}
          />
          <Text color='primary' weight='bold'>
            {formatMessage(
              { id: 'Lawyer.AuditReports.Pagination.TotalEntries' },
              {
                totalEntries: data?.cases.total
              }
            )}
          </Text>
        </Stack>
      </Sidebar>
    </Card>
  )
}
